/**
 * Typed API client.
 *
 * A thin fetch wrapper that adds the base URL, JSON handling, credentials, and
 * consistent error shaping. The backend returns errors as
 * `{ error: { code, message, details?, requestId } }`; we surface that as an
 * ApiError so callers (and TanStack Query) get a clean message.
 */

import { MOCK_ENABLED, mockResponse } from './mock';
import { IS_PORTAL, getActiveStoreId, resolvePath } from './mode';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * Resolve the Shopify App Bridge session token. When embedded, App Bridge v4
 * exposes a global `shopify.idToken()`; outside the admin (local dev) it's
 * absent and we send no token (the backend rejects, which is expected there).
 */
async function getSessionToken(): Promise<string | null> {
  const bridge = (globalThis as { shopify?: { idToken?: () => Promise<string> } }).shopify;
  if (bridge?.idToken) {
    try {
      return await bridge.idToken();
    } catch {
      return null;
    }
  }
  return null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ApiErrorBody {
  error?: { code?: string; message?: string; details?: unknown; requestId?: string };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Dev-only: short-circuit to mock data when VITE_MOCK=1.
  if (MOCK_ENABLED) {
    const mock = mockResponse(path);
    if (mock !== undefined) {
      await new Promise((r) => setTimeout(r, 300)); // tiny delay so loaders show
      return mock as T;
    }
  }

  // In portal mode we authenticate by session cookie (no Shopify token) and
  // route /api/* calls to the multi-store portal API for the active store.
  const token = IS_PORTAL ? null : await getSessionToken();
  const storeId = IS_PORTAL ? getActiveStoreId() : null;
  const resolved = resolvePath(path);

  const res = await fetch(`${API_BASE}${resolved}`, {
    ...init,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(storeId ? { 'x-store-id': storeId } : {}),
      ...(init?.headers ?? {}),
    },
  });

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await res.json() : undefined;

  if (!res.ok) {
    const err = (body as ApiErrorBody)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'UNKNOWN',
      err?.message ?? `Request failed (${res.status})`,
      err?.details,
      err?.requestId,
    );
  }

  return body as T;
}

const withBody = (method: string, data?: unknown): RequestInit =>
  data === undefined ? { method } : { method, body: JSON.stringify(data) };

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, data?: unknown) => request<T>(path, withBody('POST', data)),
  patch: <T>(path: string, data?: unknown) => request<T>(path, withBody('PATCH', data)),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
